import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from 'sonner';
import { Eye, EyeOff, Pencil, Trash2, UserPlus, Copy, Check, KeyRound } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Liste des modules disponibles (clés doivent matcher backend `_MODULE_URL_PREFIXES`)
// NOTE: 'dashboard' est TOUJOURS accessible (comme Settings), donc pas dans la liste.
const AVAILABLE_MODULES = [
  { key: "planning", label: "Planning", icon: "📆" },
  { key: "gestion-equipes", label: "Sous-traitance", icon: "🤝" },
  { key: "photos", label: "Photos", icon: "📸" },
  { key: "facturation", label: "Facturation", icon: "💰" },
  { key: "bilan", label: "Bilan", icon: "📊" },
  { key: "parures-sales", label: "Parures Sales", icon: "🛏️" },
  { key: "employees", label: "Employées", icon: "👥" },
  { key: "clients", label: "Clients", icon: "👤" },
  { key: "housings", label: "Logements", icon: "🏘️" },
  { key: "ical-sync", label: "Synchro iCal", icon: "🔄" },
  { key: "stock-clients", label: "Stock Clients", icon: "🧴" },
  { key: "citations", label: "Citations", icon: "💬" },
  { key: "integrations", label: "Intégrations", icon: "🔗" },
];

const ALL_MODULE_KEYS = AVAILABLE_MODULES.map(m => m.key);

const UsersManager = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [formData, setFormData] = useState({ email: "", password: "", full_name: "", role: "user" });
  const [fullAccess, setFullAccess] = useState(true);
  const [selectedModules, setSelectedModules] = useState([...ALL_MODULE_KEYS]);
  const [showPassword, setShowPassword] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => { if (isAdmin()) loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      // Role admin → accès total forcé (allowed_modules null)
      if (formData.role === "admin") {
        payload.allowed_modules = null;
      } else {
        payload.allowed_modules = fullAccess ? null : selectedModules;
      }
      await axios.post(`${API}/auth/register`, payload);
      toast.success("Utilisateur cree avec succes");
      setShowModal(false);
      setFormData({ email: "", password: "", full_name: "", role: "user" });
      setFullAccess(true);
      setSelectedModules([...ALL_MODULE_KEYS]);
      setShowPassword(false);
      await loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la creation");
    }
  };

  const handleOpenEdit = (user) => {
    const userAllowed = user.allowed_modules;
    setEditUser({
      ...user,
      newPassword: user.password_plain || "",
      _fullAccess: userAllowed === null || userAllowed === undefined,
      _selectedModules: Array.isArray(userAllowed) ? userAllowed : [...ALL_MODULE_KEYS],
    });
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        full_name: editUser.full_name,
        role: editUser.role
      };
      if (editUser.newPassword) {
        payload.password = editUser.newPassword;
      }
      // Permissions : admin → accès total forcé ; sinon selon toggle
      if (editUser.role === "admin") {
        payload.allowed_modules = null;
      } else {
        payload.allowed_modules = editUser._fullAccess ? null : editUser._selectedModules;
      }
      await axios.put(`${API}/users/${editUser.id}`, payload);
      toast.success("Utilisateur mis a jour");
      setEditUser(null);
      await loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise a jour");
    }
  };

  const toggleModule = (moduleKey, isForEdit = false) => {
    if (isForEdit) {
      setEditUser(prev => {
        const current = prev._selectedModules || [];
        const next = current.includes(moduleKey)
          ? current.filter(k => k !== moduleKey)
          : [...current, moduleKey];
        return { ...prev, _selectedModules: next };
      });
    } else {
      setSelectedModules(prev =>
        prev.includes(moduleKey) ? prev.filter(k => k !== moduleKey) : [...prev, moduleKey]
      );
    }
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser.id) {
      toast.error("Vous ne pouvez pas supprimer votre propre compte");
      return;
    }
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Utilisateur supprime");
      await loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const togglePasswordVisibility = (userId) => {
    setVisiblePasswords(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const copyPassword = (password, userId) => {
    navigator.clipboard.writeText(password);
    setCopiedId(userId);
    toast.success("Mot de passe copie");
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (!isAdmin()) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4">
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acces Restreint</h2>
          <p className="text-gray-600">Cette page est reservee aux administrateurs.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div></div>;
  }

  return (
    <div className="max-w-5xl mx-auto py-6 px-4" data-testid="users-manager">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Utilisateurs</h1>
          <p className="text-gray-500 mt-1">{users.length} compte{users.length > 1 ? 's' : ''} actif{users.length > 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors" data-testid="add-user-btn">
          <UserPlus className="h-4 w-4" /> Nouveau
        </button>
      </div>

      {/* Liste des utilisateurs */}
      <div className="space-y-3">
        {users.map((user) => (
          <div key={user.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4" data-testid={`user-row-${user.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${user.role === 'admin' ? 'bg-red-600' : 'bg-blue-500'}`}>
                  {(user.full_name || user.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900">{user.full_name || "Sans nom"}</p>
                    {user.id === currentUser.id && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Vous</span>}
                    <span className={`text-xs px-1.5 py-0.5 rounded ${user.role === 'admin' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {user.role === 'admin' ? 'Admin' : user.role === 'super_admin' ? 'Super Admin' : 'Utilisateur'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate">{user.email}</p>
                  {/* Mot de passe inline */}
                  <div className="flex items-center gap-2 mt-1.5">
                    <KeyRound className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    {user.password_plain ? (
                      <>
                        <span className="text-sm font-mono text-gray-600" data-testid={`user-password-display-${user.id}`}>
                          {visiblePasswords[user.id] ? user.password_plain : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                        </span>
                        <button onClick={() => togglePasswordVisibility(user.id)} className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors" data-testid={`toggle-password-${user.id}`}>
                          {visiblePasswords[user.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                        <button onClick={() => copyPassword(user.password_plain, user.id)} className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors" data-testid={`copy-password-${user.id}`}>
                          {copiedId === user.id ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-amber-600 italic">Mot de passe non visible - modifier pour definir</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                <button onClick={() => handleOpenEdit(user)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-testid={`edit-user-${user.id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(user.id)} disabled={user.id === currentUser.id}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`delete-user-${user.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal creation */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-lg font-bold mb-4">Nouvel utilisateur</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
                <input type="text" required value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Jean Dupont" data-testid="user-fullname-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="jean@example.com" data-testid="user-email-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} required value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-10" placeholder="Mot de passe" data-testid="user-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="user-role-select">
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                </select>
                {formData.role === "admin" && (
                  <p className="text-xs text-gray-500 mt-1 italic">Les administrateurs ont automatiquement accès total.</p>
                )}
              </div>

              {/* Permissions modulaires — visible uniquement pour role=user */}
              {formData.role === "user" && (
                <div className="border-t pt-4">
                  <label className="flex items-center gap-2 cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={fullAccess}
                      onChange={(e) => setFullAccess(e.target.checked)}
                      className="h-4 w-4 text-red-600 rounded"
                      data-testid="user-full-access-toggle"
                    />
                    <span className="text-sm font-medium text-gray-700">Accès total à l'application</span>
                  </label>
                  {!fullAccess && (
                    <div data-testid="user-modules-grid" className="grid grid-cols-2 gap-2 pl-6 max-h-56 overflow-y-auto">
                      {AVAILABLE_MODULES.map(mod => (
                        <label key={mod.key} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                          <input
                            type="checkbox"
                            checked={selectedModules.includes(mod.key)}
                            onChange={() => toggleModule(mod.key, false)}
                            className="h-4 w-4 text-red-600 rounded"
                            data-testid={`user-module-cb-${mod.key}`}
                          />
                          <span>{mod.icon} {mod.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowModal(false); setShowPassword(false); }} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700" data-testid="save-user-btn">Creer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal edition */}
      {editUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-lg font-bold mb-4">Modifier l'utilisateur</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
                <input type="text" value={editUser.full_name || ""} onChange={(e) => setEditUser({ ...editUser, full_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="edit-fullname-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={editUser.email} disabled className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500" />
                <p className="text-xs text-gray-400 mt-1">L'email ne peut pas etre modifie</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                <div className="relative">
                  <input type="text" value={editUser.newPassword || ""} onChange={(e) => setEditUser({ ...editUser, newPassword: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-10 font-mono"
                    placeholder={editUser.password_plain ? "" : "Definir un nouveau mot de passe"} data-testid="edit-password-input" />
                </div>
                {!editUser.password_plain && (
                  <p className="text-xs text-amber-600 mt-1">Ancien mot de passe non disponible. Saisissez-en un nouveau.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select value={editUser.role} onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="edit-role-select">
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                </select>
                {editUser.role === "admin" && (
                  <p className="text-xs text-gray-500 mt-1 italic">Les administrateurs ont automatiquement accès total.</p>
                )}
              </div>

              {/* Permissions modulaires — visible uniquement pour role=user */}
              {editUser.role === "user" && (
                <div className="border-t pt-4">
                  <label className="flex items-center gap-2 cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={!!editUser._fullAccess}
                      onChange={(e) => setEditUser({ ...editUser, _fullAccess: e.target.checked })}
                      className="h-4 w-4 text-red-600 rounded"
                      data-testid="edit-full-access-toggle"
                    />
                    <span className="text-sm font-medium text-gray-700">Accès total à l'application</span>
                  </label>
                  {!editUser._fullAccess && (
                    <div data-testid="edit-modules-grid" className="grid grid-cols-2 gap-2 pl-6 max-h-56 overflow-y-auto">
                      {AVAILABLE_MODULES.map(mod => (
                        <label key={mod.key} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                          <input
                            type="checkbox"
                            checked={(editUser._selectedModules || []).includes(mod.key)}
                            onChange={() => toggleModule(mod.key, true)}
                            className="h-4 w-4 text-red-600 rounded"
                            data-testid={`edit-module-cb-${mod.key}`}
                          />
                          <span>{mod.icon} {mod.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setEditUser(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button onClick={handleSaveEdit} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="save-edit-btn">Sauvegarder</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManager;
